import { useState, useRef } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

const ORA = "#FB923C"; // Oracle accent

function highlightSQL(code) {
  const kws = ["SELECT","FROM","WHERE","AND","OR","NOT","IN","IS","NULL","LIKE","BETWEEN",
    "ORDER","BY","GROUP","HAVING","DISTINCT","AS","JOIN","LEFT","RIGHT","FULL","INNER",
    "OUTER","CROSS","SELF","NATURAL","ON","UNION","ALL","INTERSECT","MINUS","EXCEPT",
    "INSERT","INTO","VALUES","UPDATE","SET","DELETE","MERGE","USING","MATCHED","WHEN",
    "THEN","CREATE","TABLE","ALTER","DROP","TRUNCATE","RENAME","ADD","MODIFY","COLUMN",
    "CONSTRAINT","PRIMARY","KEY","FOREIGN","REFERENCES","UNIQUE","CHECK","DEFAULT","NOT",
    "GRANT","REVOKE","TO","COMMIT","ROLLBACK","SAVEPOINT","BEGIN","END","DECLARE","IF",
    "ELSIF","ELSE","LOOP","FOR","WHILE","RETURN","PROCEDURE","FUNCTION","TRIGGER",
    "PACKAGE","BODY","CURSOR","EXCEPTION","RAISE","BEFORE","AFTER","EACH","ROW",
    "INSTEAD","OF","SEQUENCE","INDEX","VIEW","SYNONYM","PUBLIC","REPLACE","OR","CASCADE",
    "ENABLE","DISABLE","PURGE","EXEC","DESC","START","WITH","INCREMENT","NOCACHE","NOCYCLE",
    "IDENTIFIED","SESSION","ROLE","PARTITION","OVER","ROWS","UNBOUNDED","PRECEDING","FOLLOWING","CURRENT",
    "IN","OUT","NUMBER","VARCHAR2","CHAR","DATE","CLOB","BLOB","TIMESTAMP","BOOLEAN",
    "TYPE","ROWCOUNT","ROWNUM","ROWID","SYSDATE","SYSTIMESTAMP","DUAL","USER","OTHERS",
    "NO_DATA_FOUND","AUTONOMUS_TRANSACTION","PRAGMA","NEXTVAL","CURRVAL","LEVEL",
    "CONNECT","PRIOR","START"];
  const fns = ["UPPER","LOWER","INITCAP","LENGTH","SUBSTR","INSTR","CONCAT","TRIM","LPAD","RPAD",
    "REPLACE","ROUND","TRUNC","MOD","CEIL","FLOOR","ABS","POWER","ADD_MONTHS","MONTHS_BETWEEN",
    "NEXT_DAY","LAST_DAY","TO_CHAR","TO_DATE","TO_NUMBER","NVL","NVL2","COALESCE","NULLIF",
    "DECODE","CASE","COUNT","SUM","AVG","MAX","MIN","RANK","DENSE_RANK","ROW_NUMBER",
    "DBMS_OUTPUT","PUT_LINE","RAISE_APPLICATION_ERROR","SQLERRM","INITCAP","FETCH","OPEN","CLOSE"];

  return code.split("\n").map((line, li) => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith("--")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i]==="-" && line[i+1]==="-") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i=line.length; continue;
      }
      if (line[i]==="'" ) {
        let j=i+1;
        while(j<line.length && line[j]!=="'") j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i=j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j=i; while(j<line.length && /[\d_.,]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i=j; continue;
      }
      if (/[a-zA-Z_:]/.test(line[i])) {
        let j=i; while(j<line.length && /[\w%:]/.test(line[j])) j++;
        const word=line.slice(i,j);
        const upper=word.toUpperCase();
        let color=T.textPri;
        if (kws.includes(upper)) color=T.codeKw;
        else if (fns.includes(upper)) color=T.codeFn;
        else if (word.startsWith(":")) color=T.orange;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i=j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

function CodeBlock({ code, label="sql" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:14,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"5px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"12px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlightSQL(code)}
      </div>
    </div>
  );
}

function Note({ children, color=T.accent }) {
  return (
    <div style={{background:`${color}10`,border:`1px solid ${color}33`,borderLeft:`3px solid ${color}`,
      borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:13,color:T.textSec,lineHeight:1.7}}>
      {children}
    </div>
  );
}

function Story({ children }) {
  return (
    <div style={{background:"rgba(167,139,250,.08)",border:"1px solid rgba(167,139,250,.25)",
      borderLeft:"3px solid #A78BFA",borderRadius:8,padding:"10px 14px",marginBottom:14,
      fontSize:13,color:"#C4B5FD",lineHeight:1.7}}>
      <span style={{fontWeight:700,color:"#A78BFA",marginRight:6}}>📖 Story:</span>{children}
    </div>
  );
}

function Def({ children }) {
  return (
    <div style={{background:"rgba(56,189,248,.08)",border:"1px solid rgba(56,189,248,.25)",
      borderLeft:"3px solid #38BDF8",borderRadius:8,padding:"10px 14px",marginBottom:14,
      fontSize:13,color:T.textSec,lineHeight:1.7}}>
      <span style={{fontWeight:700,color:T.accent,marginRight:6}}>📌 Definition:</span>{children}
    </div>
  );
}

function H2({ children }) {
  return <h2 style={{fontSize:18,fontWeight:700,color:T.textPri,margin:"24px 0 12px",
    borderBottom:`1px solid ${T.border}`,paddingBottom:6}}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{fontSize:15,fontWeight:700,color:T.accent,margin:"18px 0 10px"}}>{children}</h3>;
}
function P({ children }) {
  return <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.8,margin:"0 0 12px"}}>{children}</p>;
}
function C({ children }) {
  return <code style={{background:"#0D1929",color:T.accent,padding:"1px 6px",borderRadius:4,
    fontFamily:"monospace",fontSize:12.5}}>{children}</code>;
}

function DTable({ headers, rows }) {
  return (
    <div style={{overflowX:"auto",marginBottom:16}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
        <thead>
          <tr>{headers.map((h,i)=>(
            <th key={i} style={{background:"#0D1929",color:T.accent,padding:"8px 12px",
              textAlign:"left",borderBottom:`1px solid ${T.border}`,fontWeight:600,whiteSpace:"nowrap"}}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>{rows.map((row,ri)=>(
          <tr key={ri} style={{borderBottom:`1px solid ${T.border}33`}}>
            {row.map((cell,ci)=>(
              <td key={ci} style={{padding:"8px 12px",color:T.textSec,lineHeight:1.6,
                background:ri%2===0?"transparent":"rgba(30,45,69,.3)",verticalAlign:"top"}}>
                {cell}
              </td>
            ))}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ── PART COMPONENTS ───────────────────────────────────────────────────────────

function P0() { return (
  <div>
    <H2>0.1 Data vs Information</H2>
    <P><strong style={{color:T.textPri}}>Data</strong> = raw facts. <C>Rahul</C>, <C>12</C>, <C>85</C></P>
    <P><strong style={{color:T.textPri}}>Information</strong> = data with meaning. "Rahul, age 12, scored 85 marks."</P>
    <Note>Data alone is useless. Information is what you make decisions with.</Note>

    <H2>0.2 What is a Database?</H2>
    <Story>There is a cupboard in your house with separate drawers — one for school certificates, one for electricity bills, one for photographs. Everything is organised, so that whenever you need something, you find it immediately.</Story>
    <Def>A database is an organised collection of related data, stored so it can be easily retrieved, updated, and managed.</Def>

    <H2>0.3 What is a DBMS?</H2>
    <P>The cupboard is the database. The person who manages the cupboard is the <strong style={{color:ORA}}>DBMS</strong> (Database Management System).</P>
    <P>A DBMS is software that: stores data, fetches it when you ask (queries), refuses bad data (rules), keeps things consistent with 100 simultaneous users, and handles backup and security.</P>

    <H2>0.4 File System Problems — Why We Needed DBMS</H2>
    <DTable
      headers={["Problem","What it means"]}
      rows={[
        ["Data redundancy","The same student's name repeated across 5 files"],
        ["Data inconsistency","One file says 'Rahul', another says 'Rahul Kumar' — which is right?"],
        ["No security","Anyone who opens the file sees everything"],
        ["No concurrent access","Two people edit at once, the file gets corrupted"],
        ["Hard to search","Searching 10 lakh rows becomes painful"],
      ]}
    />

    <H2>0.5 What is an RDBMS?</H2>
    <P><strong style={{color:ORA}}>R = Relational.</strong></P>
    <Story>A school keeps one register of Students and another of Classes. The student register says "Rahul — Class ID 7". The class register says "Class ID 7 = Class 7-A, teacher Meena". The two registers are <strong>related</strong> through one small code.</Story>
    <Def>An RDBMS is a DBMS where data is stored in <strong>tables (relations)</strong>, linked through <strong>keys</strong>. Based on E. F. Codd's relational model.</Def>
    <P>Examples: <strong style={{color:ORA}}>Oracle</strong>, MySQL, PostgreSQL, SQL Server, IBM Db2.</P>

    <H2>0.6 Oracle Database — in one line</H2>
    <P>A commercial RDBMS from Oracle Corporation. Banks, telecom, airlines — wherever data errors are unacceptable, you find Oracle. Its SQL flavour differs slightly from others, and it ships with its own programming language: <strong style={{color:ORA}}>PL/SQL</strong>.</P>

    <H2>0.7 Oracle's Structure (the basics)</H2>
    <DTable
      headers={["Term","What it is","Analogy"]}
      rows={[
        [<C>Database</C>,"Files sitting on disk — the actual data","The cupboard"],
        [<C>Instance</C>,"Memory (SGA) + background processes","The guard and his desk"],
        [<C>Schema</C>,"All objects owned by one user. User = Schema","HR.EMPLOYEES = HR's cupboard"],
        ["Tablespace → Segment → Extent → Block","Oracle's storage ladder","Building → Floor → Room → Cupboard"],
      ]}
    />
    <Note color={T.yellow}>If the instance shuts down, the data is safe — you just can't reach it. Remember: <C>user = schema</C>.</Note>
  </div>
);}

function P1() { return (
  <div>
    <H2>1.1 What is a Table?</H2>
    <Story>Open your class attendance register. Across the top are headings — Roll No, Name, Class, Marks. Below, each line holds one child's details. That register IS a table.</Story>
    <DTable
      headers={["Term","Everyday word","Technical name"]}
      rows={[
        ["Table","The whole register","Relation"],
        ["Row","One child's line","Record / Tuple"],
        ["Column","One heading (like Marks)","Field / Attribute"],
        ["Cell","One box's value","Value"],
        ["Degree","How many columns","Degree"],
        ["Cardinality","How many rows","Cardinality"],
      ]}
    />
    <Def>A table is a collection of related data organised in rows and columns, where each row is a unique record and each column holds one specific type of information.</Def>
    <CodeBlock code={`CREATE TABLE student (
    roll_no   NUMBER(4),
    name      VARCHAR2(50),
    class     VARCHAR2(10),
    marks     NUMBER(5,2),
    join_date DATE
);`} />

    <H2>1.2 Datatypes — the "type" of each column</H2>
    <P>You must declare up front what a column will hold — number, text, or date. Like a pencil box holds pencils and a tiffin holds food.</P>
    <DTable
      headers={["Oracle Datatype","What it holds","Example"]}
      rows={[
        [<C>VARCHAR2(n)</C>,"Variable-length text, max n chars","VARCHAR2(50) for a name"],
        [<C>CHAR(n)</C>,"Fixed-length text (padded with blanks)","CHAR(1) for 'M'/'F'"],
        [<C>NUMBER(p,s)</C>,"Number — p=total digits, s=decimal digits","NUMBER(5,2) allows 999.99"],
        [<C>DATE</C>,"Date and time (to the second)","SYSDATE"],
        [<C>TIMESTAMP</C>,"Date and time with fractional seconds","Good for logs"],
        [<C>CLOB</C>,"Very large text (books)","An article body"],
        [<C>BLOB</C>,"Binary — photo, video, PDF","A profile picture"],
      ]}
    />
    <Note color={ORA}>Oracle tip: Never use <C>VARCHAR</C>; always use <C>VARCHAR2</C>. That is Oracle's own official recommendation.</Note>

    <H2>1.3 NULL — the most confusing idea</H2>
    <Note color={T.red}><strong>NULL does not mean zero. It is not a blank space. NULL means "unknown / no value at all."</strong></Note>
    <Story>In the attendance register you left the marks box empty for a child. Does that mean he scored 0? No — he hasn't taken the exam yet.</Story>
    <P>Therefore:</P>
    <ul style={{paddingLeft:20,marginBottom:12}}>
      <li style={{color:T.textSec,lineHeight:1.8,fontSize:13.5}}><C>NULL = NULL</C> is not TRUE and not FALSE — it is <strong style={{color:T.yellow}}>UNKNOWN</strong>. How can two "don't knows" be equal?</li>
      <li style={{color:T.textSec,lineHeight:1.8,fontSize:13.5}}>Use <C>IS NULL</C> / <C>IS NOT NULL</C> for comparison, never <C>= NULL</C>.</li>
      <li style={{color:T.textSec,lineHeight:1.8,fontSize:13.5}}>Any arithmetic touching NULL returns NULL. <C>1000 + NULL = NULL</C>.</li>
    </ul>
    <CodeBlock code={`SELECT * FROM student WHERE marks IS NULL;   -- correct
SELECT * FROM student WHERE marks = NULL;    -- always 0 rows, a mistake`} />
  </div>
);}

function P2() { return (
  <div>
    <P style={{fontSize:14,color:T.textSec}}>A key is the column (or columns) used to identify a row or to link rows together.</P>
    <H2>2.1 Types of Keys</H2>
    <Story>A class can have two boys named "Rahul". The name won't identify them. But a <strong>roll number</strong> never repeats — that is the real identity.</Story>
    <DTable
      headers={["Key","What it is","Analogy"]}
      rows={[
        [<strong style={{color:T.accent}}>Super Key</strong>,"Any set of columns that makes a row unique","Roll No + Name (extras allowed)"],
        [<strong style={{color:T.accent}}>Candidate Key</strong>,"A minimal super key — no unnecessary column","Roll No, Aadhaar No — both are candidates"],
        [<strong style={{color:T.green}}>Primary Key</strong>,"The one candidate key you choose","Roll No is chosen"],
        [<strong style={{color:T.textSec}}>Alternate Key</strong>,"The candidate keys left over","Aadhaar No"],
        [<strong style={{color:T.yellow}}>Composite Key</strong>,"Two or more columns forming the PK","(Order_ID + Product_ID)"],
        [<strong style={{color:ORA}}>Foreign Key</strong>,"A reference to another table's primary key","Class_ID inside the student register"],
        [<strong style={{color:T.purple}}>Surrogate Key</strong>,"Artificial ID with no business meaning","Auto-generated EMP_ID"],
      ]}
    />
    <Note color={T.green}>
      <strong>Two golden rules for a Primary Key:</strong> it must be UNIQUE and it can never be NULL. A table can have only <strong>one</strong> primary key.
    </Note>
    <Note color={ORA}>
      <strong>Foreign Key = referential integrity.</strong> The value in the child table's FK column must exist in the parent table's PK column.
    </Note>
  </div>
);}

function P3() { return (
  <div>
    <H2>3.1 What is a Constraint?</H2>
    <Story>School rules — "ID card is compulsory", "no two children get the same roll number", "age cannot be below 5". A guard stands at the gate; break a rule and you don't get in.</Story>
    <Def>Constraints are rules applied on table columns that the database itself enforces. Whether the data comes from an application, a script, or someone typing by hand — the rule applies to all. You can forget a validation in code; a constraint never forgets.</Def>

    <H2>3.2 Oracle's 5 + 1 Constraints</H2>
    <DTable
      headers={["Constraint","Rule","Story"]}
      rows={[
        [<C>NOT NULL</C>,"Column cannot be left empty","Name is mandatory on the form"],
        [<C>UNIQUE</C>,"Value cannot repeat (NULLs allowed)","Everyone has a different mobile number"],
        [<C>PRIMARY KEY</C>,"UNIQUE + NOT NULL","Roll number"],
        [<C>FOREIGN KEY</C>,"Value must exist in the parent table","Class 15-A doesn't exist — rejected"],
        [<C>CHECK</C>,"A custom condition must be satisfied","Marks between 0 and 100"],
        [<C>DEFAULT</C>,"Used when you supply nothing","Country = 'India'"],
      ]}
    />

    <H2>3.3 A Full Example</H2>
    <CodeBlock code={`CREATE TABLE class (
    class_id   NUMBER(3) PRIMARY KEY,
    class_name VARCHAR2(10) NOT NULL,
    teacher    VARCHAR2(50)
);
CREATE TABLE student (
    roll_no   NUMBER(4),
    name      VARCHAR2(50) CONSTRAINT nn_name  NOT NULL,
    email     VARCHAR2(60) CONSTRAINT uq_email UNIQUE,
    gender    CHAR(1)      DEFAULT 'M',
    marks     NUMBER(5,2),
    class_id  NUMBER(3),
    CONSTRAINT pk_student  PRIMARY KEY (roll_no),
    CONSTRAINT ck_marks    CHECK (marks BETWEEN 0 AND 100),
    CONSTRAINT ck_gender   CHECK (gender IN ('M','F','O')),
    CONSTRAINT fk_class    FOREIGN KEY (class_id) REFERENCES class(class_id)
);`} />
    <Note color={T.yellow}>Always name your constraints (<C>pk_student</C>, <C>ck_marks</C>). When an error is raised, Oracle prints that name — debugging becomes ten times easier.</Note>

    <H2>3.4 Foreign Key Delete Rules</H2>
    <DTable
      headers={["Option","Behaviour"]}
      rows={[
        ["(default)","The delete is refused — error"],
        [<C>ON DELETE CASCADE</C>,"Parent goes, children are deleted automatically"],
        [<C>ON DELETE SET NULL</C>,"Children survive, their FK becomes NULL"],
      ]}
    />
    <CodeBlock code={`CONSTRAINT fk_class FOREIGN KEY (class_id)
    REFERENCES class(class_id) ON DELETE CASCADE`} />

    <H2>3.5 Adding / Removing Constraints Later</H2>
    <CodeBlock code={`ALTER TABLE student ADD CONSTRAINT ck_marks2 CHECK (marks <= 100);
ALTER TABLE student DROP CONSTRAINT ck_marks2;
ALTER TABLE student DISABLE CONSTRAINT ck_marks;   -- temporarily off
ALTER TABLE student ENABLE  CONSTRAINT ck_marks;
SELECT constraint_name, constraint_type, status
FROM   user_constraints WHERE table_name = 'STUDENT';
-- P=Primary  R=Foreign(Referential)  U=Unique  C=Check/NotNull`} />
  </div>
);}

function P4() { return (
  <div>
    <H2>4.1 The Five Families of SQL</H2>
    <Story>In a school, different jobs belong to different people — the principal sets up the structure, the teacher writes the data, the student reads it, the guard decides who may enter, and the accountant finalises or cancels the money.</Story>
    <DTable
      headers={["Family","Full form","Commands","Job"]}
      rows={[
        [<strong style={{color:T.accent}}>DDL</strong>,"Data Definition","CREATE ALTER DROP TRUNCATE RENAME","Build/change structure"],
        [<strong style={{color:T.green}}>DML</strong>,"Data Manipulation","INSERT UPDATE DELETE MERGE","Move data in and out"],
        [<strong style={{color:T.yellow}}>DQL</strong>,"Data Query","SELECT","Read data"],
        [<strong style={{color:T.purple}}>DCL</strong>,"Data Control","GRANT REVOKE","Give/take permissions"],
        [<strong style={{color:ORA}}>TCL</strong>,"Transaction Control","COMMIT ROLLBACK SAVEPOINT","Confirm or cancel work"],
      ]}
    />
    <Note color={T.red}><strong>Critical Oracle rule:</strong> DDL <strong>auto-commits</strong>. The moment you run CREATE/ALTER/DROP/TRUNCATE, all pending work is permanently saved. There is no rollback.</Note>

    <H2>4.2 DDL Commands</H2>
    <CodeBlock code={`CREATE TABLE ...                                     -- new table
ALTER TABLE student ADD (phone VARCHAR2(15));        -- new column
ALTER TABLE student MODIFY (name VARCHAR2(100));     -- change size
ALTER TABLE student RENAME COLUMN name TO full_name;
ALTER TABLE student DROP COLUMN phone;
TRUNCATE TABLE student;   -- all data gone, structure stays
DROP TABLE student;       -- the table itself disappears
DROP TABLE student PURGE; -- gone from the recycle bin too`} />

    <H3>DELETE vs TRUNCATE vs DROP — a favourite interview question</H3>
    <DTable
      headers={["","DELETE","TRUNCATE","DROP"]}
      rows={[
        ["Type","DML","DDL","DDL"],
        ["What goes","Selected rows","All rows","Rows + structure"],
        ["WHERE allowed?","Yes","No","No"],
        ["Rollback?","Yes","No","No"],
        ["Speed","Slow","Very fast","Fast"],
        ["Analogy","Erasing a few lines","Tearing out all the pages","Burning the register"],
      ]}
    />

    <H2>4.3 DML Commands</H2>
    <CodeBlock code={`-- INSERT
INSERT INTO student (roll_no, name, email, marks, class_id)
VALUES (1, 'Rahul', 'rahul@abc.com', 85, 7);

-- UPDATE  (forget the WHERE and the whole table gets updated!)
UPDATE student SET marks = 90 WHERE roll_no = 1;

-- DELETE
DELETE FROM student WHERE roll_no = 1;

-- MERGE (UPSERT): update if it exists, insert if it doesn't
MERGE INTO student s
USING new_data n ON (s.roll_no = n.roll_no)
WHEN MATCHED    THEN UPDATE SET s.marks = n.marks
WHEN NOT MATCHED THEN INSERT (roll_no, name) VALUES (n.roll_no, n.name);`} />

    <H2>4.4 SELECT — Reading Data</H2>
    <P>The numbers show the <strong style={{color:T.yellow}}>execution order</strong>. You write SELECT first, but Oracle reads FROM first. That is exactly why you cannot use a column alias in WHERE — at that moment SELECT hasn't run yet.</P>
    <CodeBlock code={`SELECT   column_list          -- 6. what to display
FROM     table                -- 1. from where
WHERE    row_condition        -- 2. filter rows
GROUP BY column               -- 3. form groups
HAVING   group_condition      -- 4. filter groups
ORDER BY column               -- 7. sort
FETCH FIRST n ROWS ONLY;      -- 8. top n

-- Real example:
SELECT name, marks FROM student
WHERE  marks > 80
  AND  class_id IN (7, 8)
  AND  name LIKE 'R%'          -- starts with R
  AND  join_date BETWEEN DATE '2025-01-01' AND DATE '2025-12-31'
ORDER BY marks DESC;

SELECT DISTINCT class_id FROM student;   -- remove duplicates`} />
    <Note color={T.yellow}>In Oracle, <C>DUAL</C> is a dummy table for testing: <C>SELECT SYSDATE FROM DUAL;</C></Note>
    <H3>Operator Cheat Sheet</H3>
    <P><C>= != &gt; &lt; &gt;= &lt;=</C> | <C>BETWEEN..AND</C> | <C>IN (list)</C> | <C>LIKE</C> (<C>%</C> = any chars, <C>_</C> = exactly one) | <C>IS NULL</C> | <C>AND OR NOT</C></P>
  </div>
);}

function P5() { return (
  <div>
    <H2>5.1 What is a Function?</H2>
    <Story>A juicer. Put an orange in, get juice out. What you put in is the input/argument, what comes out is the return value. You never need to know how the machine works inside.</Story>
    <DTable
      headers={["","Single-Row Function","Multi-Row (Aggregate) Function"]}
      rows={[
        ["Input","One row","A group of rows"],
        ["Output","One answer per row","One answer for the whole group"],
        ["Example","UPPER(name) — 10 rows → 10 answers","SUM(marks) — 10 rows → 1 answer"],
      ]}
    />

    <H2>5.2 Single-Row Functions</H2>
    <H3>String Functions</H3>
    <CodeBlock code={`UPPER('rahul')              -- RAHUL
LOWER('RAHUL')              -- rahul
INITCAP('rahul kumar')      -- Rahul Kumar
LENGTH('Rahul')             -- 5
SUBSTR('Database', 1, 4)    -- Data  (4 chars from position 1)
INSTR('Database', 'b')      -- 5    (where 'b' was found)
CONCAT('A','B')  /  'A'||'B'-- AB   (|| is Oracle's join operator)
TRIM('  hi  ')              -- hi
LPAD('7', 3, '0')           -- 007
REPLACE('2025-01-01','-','/')`} />

    <H3>Number Functions</H3>
    <CodeBlock code={`ROUND(45.678, 2)   -- 45.68
TRUNC(45.678, 2)   -- 45.67  (chopped off, not rounded)
MOD(10, 3)         -- 1      (remainder)
CEIL(45.1)         -- 46
FLOOR(45.9)        -- 45
ABS(-10)           -- 10
POWER(2,3)         -- 8`} />

    <H3>Date Functions</H3>
    <CodeBlock code={`SYSDATE                          -- current date and time (DB server)
SYSTIMESTAMP                     -- with time zone
ADD_MONTHS(SYSDATE, 3)           -- three months ahead
MONTHS_BETWEEN(d1, d2)           -- months between two dates
NEXT_DAY(SYSDATE, 'MONDAY')      -- the next Monday
LAST_DAY(SYSDATE)                -- last day of this month
TRUNC(SYSDATE)                   -- strip the time, keep the date
SYSDATE + 7                      -- seven days ahead`} />

    <H3>Conversion Functions</H3>
    <CodeBlock code={`TO_CHAR(SYSDATE, 'DD-MON-YYYY HH24:MI:SS')   -- date to text
TO_CHAR(1234.5, '99,999.99')                 -- number to text
TO_DATE('25-12-2025','DD-MM-YYYY')           -- text to date
TO_NUMBER('1234')                            -- text to number`} />

    <H3>NULL-Handling Functions</H3>
    <CodeBlock code={`NVL(marks, 0)                  -- if NULL, use 0
NVL2(marks, 'Given','Pending') -- not NULL → arg2, NULL → arg3
COALESCE(a, b, c)              -- first non-NULL value
NULLIF(a, b)                   -- returns NULL if both are equal
DECODE(grade,'A',10,'B',8,0)   -- Oracle's older if-else
CASE WHEN marks >= 90 THEN 'A'
     WHEN marks >= 75 THEN 'B'
     ELSE 'C' END              -- modern, readable`} />

    <H2>5.3 Aggregate Functions + GROUP BY</H2>
    <CodeBlock code={`COUNT(*)        -- every row (NULLs counted too)
COUNT(marks)    -- only non-NULL marks
SUM(marks)  AVG(marks)  MAX(marks)  MIN(marks)`} />
    <Note color={T.yellow}><strong>Golden rule:</strong> aggregate functions <strong>ignore NULLs</strong> — <C>COUNT(*)</C> is the only exception.</Note>
    <Story>The whole school is standing in a line. "Separate yourselves by class" = GROUP BY class. Now compute each group's average.</Story>
    <CodeBlock code={`SELECT   class_id, COUNT(*) AS total, AVG(marks) AS avg_marks
FROM     student
WHERE    marks IS NOT NULL      -- filters rows, BEFORE groups are formed
GROUP BY class_id
HAVING   AVG(marks) > 70        -- filters groups, AFTER they are formed
ORDER BY avg_marks DESC;`} />
    <Note color={T.accent}><strong>WHERE vs HAVING:</strong> WHERE filters individual rows before grouping. HAVING filters groups after grouping. That's why <C>WHERE AVG(marks) &gt; 70</C> is wrong — at that moment no average exists yet.</Note>
    <P><strong style={{color:T.textPri}}>Rule:</strong> every column in SELECT that is not inside an aggregate must appear in GROUP BY.</P>

    <H2>5.4 Analytic (Window) Functions — first look</H2>
    <P>An aggregate squeezes rows away. An analytic function keeps every row <em>and</em> gives you the group-level answer alongside it.</P>
    <CodeBlock code={`SELECT name, class_id, marks,
       RANK()       OVER (PARTITION BY class_id ORDER BY marks DESC) AS rnk,
       ROW_NUMBER() OVER (PARTITION BY class_id ORDER BY marks DESC) AS rn,
       AVG(marks)   OVER (PARTITION BY class_id) AS class_avg
FROM   student;`} />
    <Note><C>RANK</C> gives ties the same rank then skips; <C>DENSE_RANK</C> doesn't skip; <C>ROW_NUMBER</C> is always unique.</Note>
  </div>
);}

function P6() { return (
  <div>
    <H2>6.1 What is a JOIN?</H2>
    <Story>One register lists children's names and their Class ID. Another register lists, against each Class ID, the class name and the teacher. The principal asks: "Who is Rahul's teacher?" You have to open both registers and match on Class ID. That is a JOIN.</Story>
    <Def>A join combines rows from two or more tables based on a related column between them.</Def>

    <H2>6.2 Types of Joins</H2>
    <H3>INNER JOIN — matches only</H3>
    <P>A row appears only if both sides match.</P>
    <CodeBlock code={`SELECT s.name, c.class_name, c.teacher
FROM   student s
JOIN   class   c ON s.class_id = c.class_id;
-- Rahul-7A, Priya-8B. Amit (NULL class) is missing. Class 9-C (no students) is missing.`} />

    <H3>LEFT OUTER JOIN — every row from the left table</H3>
    <CodeBlock code={`SELECT s.name, c.class_name
FROM   student s
LEFT JOIN class c ON s.class_id = c.class_id;
-- Rahul-7A, Priya-8B, Amit-NULL  (Amit survives even without a class)`} />

    <H3>RIGHT OUTER JOIN / FULL OUTER JOIN</H3>
    <CodeBlock code={`-- RIGHT: includes class 9-C with a NULL student
-- FULL:  Amit AND 9-C both appear; NULLs wherever there's no match`} />

    <H3>CROSS JOIN — Cartesian Product</H3>
    <CodeBlock code={`-- 3 students x 3 classes = 9 rows
-- Mostly happens by accident when you forget the ON clause`} />

    <H3>SELF JOIN — a table joined to itself</H3>
    <Story>In an employee table, each employee's manager is also an employee in the same table.</Story>
    <CodeBlock code={`SELECT e.name AS emp, m.name AS manager
FROM   emp e
LEFT JOIN emp m ON e.manager_id = m.emp_id;`} />

    <H3>NATURAL JOIN</H3>
    <CodeBlock code={`SELECT name, class_name FROM student NATURAL JOIN class;
-- Oracle finds same-named columns itself.
-- AVOID in production — a new column tomorrow silently changes the join.`} />

    <H3>EQUI vs NON-EQUI JOIN</H3>
    <CodeBlock code={`-- Equi join: condition uses = (99% of cases)
-- Non-equi join: uses BETWEEN, >, < — e.g. salary to grade range
SELECT e.name, g.grade
FROM   emp e JOIN salary_grade g
       ON e.salary BETWEEN g.low_sal AND g.high_sal;`} />

    <H3>Join Summary</H3>
    <DTable
      headers={["Join","What you get"]}
      rows={[
        ["INNER","Only matches from both sides"],
        ["LEFT","All of left + matching right"],
        ["RIGHT","All of right + matching left"],
        ["FULL","Everything from both"],
        ["CROSS","Every combination"],
        ["SELF","A table joined to itself"],
      ]}
    />
    <Note color={T.yellow}><strong>Oracle's older syntax:</strong> <C>WHERE s.class_id = c.class_id(+)</C> — the <C>(+)</C> is Oracle's proprietary outer join. You'll see it in legacy code. Always write new code with ANSI <C>LEFT JOIN</C> syntax.</Note>

    <H2>6.3 Subqueries — a query inside a query</H2>
    <Story>"Name the child whose marks are above the class average." First compute the average (inner query), then compare against it (outer query).</Story>
    <CodeBlock code={`-- Single-row subquery (returns one value)
SELECT name FROM student
WHERE  marks > (SELECT AVG(marks) FROM student);

-- Multi-row subquery -> IN / ANY / ALL
SELECT name FROM student
WHERE  class_id IN (SELECT class_id FROM class WHERE teacher = 'Meena');

-- Correlated subquery: inner query runs again for every outer row
SELECT s.name FROM student s
WHERE  s.marks > (SELECT AVG(x.marks) FROM student x WHERE x.class_id = s.class_id);

-- EXISTS: only checks "is there any" — fast
SELECT c.class_name FROM class c
WHERE  EXISTS (SELECT 1 FROM student s WHERE s.class_id = c.class_id);

-- Inline view: a subquery in the FROM clause
SELECT * FROM (SELECT name, marks FROM student ORDER BY marks DESC)
WHERE ROWNUM <= 3;    -- top 3`} />

    <H2>6.4 Set Operators</H2>
    <P>A join combines columns side by side. A set operator stacks rows one above the other.</P>
    <DTable
      headers={["Operator","Job"]}
      rows={[
        [<C>UNION</C>,"Everything from both, duplicates removed (sorts — slower)"],
        [<C>UNION ALL</C>,"Everything from both, duplicates kept (fast)"],
        [<C>INTERSECT</C>,"Only what is common to both"],
        [<C>MINUS</C>,"In first but not the second (Oracle-specific; standard SQL calls it EXCEPT)"],
      ]}
    />
    <Note color={T.accent}>Condition: both queries must have the <strong>same number of columns</strong> and <strong>compatible datatypes</strong>.</Note>
  </div>
);}

function P7() { return (
  <div>
    <H2>7.1 View — a saved query</H2>
    <Story>You cut a small window into the cupboard door through which only the top shelf is visible. Show your little brother that window — no need to open the whole cupboard.</Story>
    <Def>A view is a virtual table. It stores no data, only the query. Every time you open the view, the underlying query runs.</Def>
    <P><strong style={{color:T.textPri}}>Why use one:</strong> security (expose only certain columns), hide complexity, reuse logic everywhere.</P>
    <CodeBlock code={`CREATE OR REPLACE VIEW v_topper AS
SELECT s.name, s.marks, c.class_name
FROM   student s JOIN class c ON s.class_id = c.class_id
WHERE  s.marks > 80;

SELECT * FROM v_topper;`} />
    <Note color={ORA}><strong>Materialized View (MV)</strong> — this one really does store data (a snapshot). It suits heavy reports: the result is pre-computed, so the query returns instantly. Must be refreshed. A very well-known Oracle feature.</Note>

    <H2>7.2 Index — the index page of a book</H2>
    <Story>You want to find "Photosynthesis" in a 500-page book. Do you turn every page (a Full Table Scan), or open the index at the back where it says "Photosynthesis — page 212" (an Index Scan)?</Story>
    <Def>An index is a separate structure that stores column values in sorted order along with ROWIDs pointing to the actual rows, so the database can find rows without scanning the whole table.</Def>
    <CodeBlock code={`CREATE INDEX idx_student_marks ON student(marks);
CREATE UNIQUE INDEX idx_email ON student(email);
CREATE INDEX idx_comp ON student(class_id, marks);   -- composite
DROP INDEX idx_student_marks;`} />
    <Note color={T.red}><strong>The trade-off:</strong> SELECT gets faster, but INSERT/UPDATE/DELETE get <strong>slower</strong> — every change also updates the index. Indexes consume disk space. Don't index every column.</Note>
    <Note color={T.yellow}>Oracle creates an index <strong>automatically</strong> for a Primary Key and a Unique constraint. It does <strong>NOT</strong> create one for a Foreign Key — you must build that yourself.</Note>

    <H2>7.3 Sequence — an automatic number generator</H2>
    <Story>The token machine at a bank. Each new customer presses the button and gets the next number — 1, 2, 3... never repeating.</Story>
    <CodeBlock code={`CREATE SEQUENCE seq_student
  START WITH 1 INCREMENT BY 1 NOCACHE NOCYCLE;

INSERT INTO student (roll_no, name)
VALUES (seq_student.NEXTVAL, 'Neha');

SELECT seq_student.CURRVAL FROM DUAL;   -- current value`} />
    <Note color={T.accent}>From Oracle 12c: <C>GENERATED ALWAYS AS IDENTITY</C> — the sequence built right into the column. Prefer this in new code.</Note>

    <H2>7.4 Synonym — a nickname</H2>
    <CodeBlock code={`CREATE SYNONYM emp FOR hr.employees_master_table;
CREATE PUBLIC SYNONYM emp FOR hr.employees_master_table;  -- for everyone`} />

    <H2>7.5 Data Dictionary — the database's own diary</H2>
    <DTable
      headers={["View","What it shows"]}
      rows={[
        [<C>USER_TABLES</C>,"My own tables"],
        [<C>ALL_TABLES</C>,"Tables I am allowed to see"],
        [<C>DBA_TABLES</C>,"Every table in the DB (DBA only)"],
        [<C>USER_CONSTRAINTS</C>,"My constraints"],
        [<C>USER_INDEXES</C>,"My indexes"],
        [<C>USER_OBJECTS</C>,"All my objects"],
      ]}
    />
    <CodeBlock code={`SELECT table_name FROM user_tables;
DESC student;      -- show the table structure (SQL*Plus command)`} />
  </div>
);}

function P8() { return (
  <div>
    <H2>8.1 What is PL/SQL?</H2>
    <P>SQL only issues one command — "give me this data". It has no if-else, no loops, no variables.</P>
    <Def>PL/SQL = Procedural Language + SQL. Oracle's own programming language which adds if-else, loops, variables, and error handling on top of SQL. And it runs inside the database, saving network round trips.</Def>
    <CodeBlock code={`DECLARE                          -- optional: variables
    v_name  student.name%TYPE;   -- %TYPE = same datatype as that column
    v_marks NUMBER := 0;
BEGIN                            -- mandatory: the logic
    SELECT name, marks INTO v_name, v_marks
    FROM   student WHERE roll_no = 1;
    IF v_marks >= 90 THEN
        DBMS_OUTPUT.PUT_LINE(v_name || ' got grade A');
    ELSIF v_marks >= 75 THEN
        DBMS_OUTPUT.PUT_LINE(v_name || ' got grade B');
    ELSE
        DBMS_OUTPUT.PUT_LINE(v_name || ' needs practice');
    END IF;
    FOR i IN 1..5 LOOP
        DBMS_OUTPUT.PUT_LINE('Count: ' || i);
    END LOOP;
EXCEPTION                        -- optional: error handling
    WHEN NO_DATA_FOUND THEN
        DBMS_OUTPUT.PUT_LINE('No such student exists');
    WHEN OTHERS THEN
        DBMS_OUTPUT.PUT_LINE('Error: ' || SQLERRM);
END;
/`} />

    <H2>8.2 Cursor — a pointer over a result set</H2>
    <Story>Running your finger down the register, reading one line at a time. The finger is the cursor.</Story>
    <P><strong style={{color:T.textPri}}>Implicit cursor:</strong> Oracle creates one automatically for every SQL statement. <C>SQL%ROWCOUNT</C> tells you how many rows were affected.</P>
    <P><strong style={{color:T.textPri}}>Explicit cursor:</strong> you declare one yourself to process a multi-row query row by row.</P>
    <CodeBlock code={`DECLARE
    CURSOR c_students IS SELECT name, marks FROM student;
BEGIN
    FOR rec IN c_students LOOP    -- cursor FOR loop: open/fetch/close automatic
        DBMS_OUTPUT.PUT_LINE(rec.name || ' - ' || rec.marks);
    END LOOP;
END;
/`} />

    <H2>8.3 Stored Procedure vs Function</H2>
    <DTable
      headers={["","Procedure","Function"]}
      rows={[
        ["Return","Not compulsory (use OUT params)","Must return exactly one value"],
        ["Where called","EXEC p_name; or inside PL/SQL","Also inside SQL: SELECT f_x(col) FROM t"],
        ["Purpose","Perform an action","Compute a value"],
      ]}
    />
    <Story>A procedure is "clean the room" (the job gets done, nothing is brought back). A function is "fetch milk from the market" (something must come back).</Story>
    <CodeBlock code={`-- PROCEDURE
CREATE OR REPLACE PROCEDURE p_raise_marks (
    p_roll IN NUMBER,
    p_by   IN NUMBER
) AS
BEGIN
    UPDATE student SET marks = marks + p_by WHERE roll_no = p_roll;
    COMMIT;
END;
/
EXEC p_raise_marks(1, 5);

-- FUNCTION
CREATE OR REPLACE FUNCTION f_get_grade (p_marks IN NUMBER)
RETURN VARCHAR2 AS
BEGIN
    RETURN CASE WHEN p_marks >= 90 THEN 'A'
                WHEN p_marks >= 75 THEN 'B'
                ELSE 'C' END;
END;
/
SELECT name, marks, f_get_grade(marks) AS grade FROM student;`} />
    <Note><strong>Parameter modes:</strong> <C>IN</C> (read-only, the default), <C>OUT</C> (send back), <C>IN OUT</C> (both).</Note>

    <H2>8.4 Package — a folder of related code</H2>
    <P>A package groups procedures, functions and variables under one name.</P>
    <P><strong style={{color:T.textPri}}>Specification</strong> = the menu card (what is available). <strong style={{color:T.textPri}}>Body</strong> = the kitchen (the actual code).</P>
    <CodeBlock code={`CREATE OR REPLACE PACKAGE pkg_student AS
    PROCEDURE p_raise_marks(p_roll NUMBER, p_by NUMBER);
    FUNCTION  f_get_grade(p_marks NUMBER) RETURN VARCHAR2;
END pkg_student;
/
CREATE OR REPLACE PACKAGE BODY pkg_student AS
    -- the real code for both goes here
END pkg_student;
/`} />

    <H2>8.5 TRIGGER — an automatic reaction</H2>
    <Story>The automatic light in a house. You don't press a switch. You just walk into the room — the sensor detects it (event) — the light turns on by itself (action). Or a CCTV: someone opens the door and recording starts on its own.</Story>
    <Def>A trigger is a named PL/SQL block that the database executes automatically when a specified event occurs on a table, view, schema, or the database itself. You never call it manually.</Def>

    <H3>The Parts of a Trigger</H3>
    <CodeBlock code={`CREATE OR REPLACE TRIGGER trg_name
BEFORE | AFTER | INSTEAD OF          -- 1. TIMING: when it runs
INSERT OR UPDATE OR DELETE           -- 2. EVENT: what it reacts to
ON student                           -- 3. TABLE
FOR EACH ROW                         -- 4. LEVEL: row-level (drop for statement-level)
WHEN (NEW.marks > 90)                -- 5. optional condition
DECLARE ... BEGIN ... END;           -- 6. BODY: what it does`} />

    <H3>Classification</H3>
    <DTable
      headers={["Basis","Types"]}
      rows={[
        ["Timing","BEFORE, AFTER, INSTEAD OF (views only)"],
        ["Event","INSERT, UPDATE, DELETE (DML) / CREATE, DROP (DDL) / LOGON, LOGOFF (system)"],
        ["Level","Row-level (FOR EACH ROW — fires once per row) vs Statement-level (fires once total)"],
      ]}
    />

    <H3>:NEW and :OLD — the heart of a trigger</H3>
    <DTable
      headers={["Event",":OLD",":NEW"]}
      rows={[
        ["INSERT","NULL","The incoming value"],
        ["UPDATE","The old value","The new value"],
        ["DELETE","The value being deleted","NULL"],
      ]}
    />
    <Note color={T.accent}>In a <strong>BEFORE trigger</strong> you can <strong>modify</strong> <C>:NEW</C> (to correct data). In an <strong>AFTER trigger</strong> you can only read it (for logging).</Note>

    <H3>Example 1 — Auto-correct data (BEFORE)</H3>
    <CodeBlock code={`CREATE OR REPLACE TRIGGER trg_clean_student
BEFORE INSERT OR UPDATE ON student
FOR EACH ROW
BEGIN
    :NEW.name  := INITCAP(TRIM(:NEW.name));   -- 'rahul ' becomes 'Rahul'
    :NEW.email := LOWER(:NEW.email);
END;
/`} />

    <H3>Example 2 — Audit Trail (AFTER)</H3>
    <CodeBlock code={`CREATE TABLE student_audit (
    roll_no    NUMBER,
    old_marks  NUMBER,
    new_marks  NUMBER,
    changed_by VARCHAR2(30),
    changed_on DATE
);
CREATE OR REPLACE TRIGGER trg_audit_marks
AFTER UPDATE OF marks ON student
FOR EACH ROW
WHEN (OLD.marks != NEW.marks)
BEGIN
    INSERT INTO student_audit
    VALUES (:OLD.roll_no, :OLD.marks, :NEW.marks, USER, SYSDATE);
END;
/`} />

    <H3>Example 3 — Enforce a Business Rule</H3>
    <CodeBlock code={`CREATE OR REPLACE TRIGGER trg_no_sunday_entry
BEFORE INSERT ON student
BEGIN
    IF TO_CHAR(SYSDATE,'DY') = 'SUN' THEN
        RAISE_APPLICATION_ERROR(-20001, 'Admission entries are not allowed on Sunday');
    END IF;
END;
/`} />

    <H3>Trigger Rules and Warnings</H3>
    <Note color={T.red}>COMMIT/ROLLBACK are NOT allowed inside a trigger (without an autonomous transaction).</Note>
    <Note color={T.yellow}><strong>Mutating table error (ORA-04091):</strong> a row-level trigger cannot SELECT from the very table it is defined on. A classic Oracle interview question.</Note>

    <H3>Constraint vs Trigger — which one when?</H3>
    <DTable
      headers={["What you need","Use"]}
      rows={[
        ["A simple rule (range, unique, not null, FK)","Constraint — faster and clearer"],
        ["Complex logic, multi-table checks, auto-logging, auto-fill","Trigger"],
      ]}
    />
  </div>
);}

function P9() { return (
  <div>
    <H2>9.1 What is a Transaction?</H2>
    <Story>Withdrawing money from an ATM. Two steps — (1) deduct Rs 500 from the account, (2) dispense Rs 500 from the machine. What if the first succeeds and the second fails? The money vanishes. So either both happen, or neither does.</Story>
    <Def>A transaction is a logical unit of work made up of one or more SQL statements that must either all succeed or all fail.</Def>

    <H2>9.2 ACID Properties</H2>
    <DTable
      headers={["Property","Meaning","Story"]}
      rows={[
        [<strong style={{color:T.accent}}>A</strong>+"tomicity","All or nothing","The ATM example"],
        [<strong style={{color:T.green}}>C</strong>+"onsistency","DB moves from one valid state to another","The rules never break"],
        [<strong style={{color:T.yellow}}>I</strong>+"solation","One transaction never sees another's half-done work","A bathroom lock — nobody walks in midway"],
        [<strong style={{color:ORA}}>D</strong>+"urability","Once committed, it survives even a power cut","Written in pen — it won't rub out"],
      ]}
    />

    <H2>9.3 TCL Commands</H2>
    <CodeBlock code={`INSERT INTO student VALUES (10, 'Neha', ...);
SAVEPOINT sp1;                 -- drop a marker here
UPDATE student SET marks = 95 WHERE roll_no = 10;
ROLLBACK TO sp1;               -- undo only the update, the insert survives
ROLLBACK;                      -- undo everything back to start
COMMIT;                        -- make everything permanent`} />
    <Note>A transaction begins with the first DML statement and ends with COMMIT, ROLLBACK, or any DDL (which auto-commits).</Note>

    <H2>9.4 Locking and Read Consistency</H2>
    <P>Two people edit the same row? Oracle applies a <strong style={{color:T.textPri}}>lock</strong> — the second one waits until the first is finished.</P>
    <Note color={T.green}><strong>Oracle's distinctive behaviour:</strong> readers do not block writers, and writers do not block readers. If you're reading while someone updates, you see the <strong>old consistent data</strong> (reconstructed from undo), never their half-finished work. This is called <strong>read consistency</strong>.</Note>
    <CodeBlock code={`SELECT * FROM student WHERE roll_no = 1 FOR UPDATE;   -- lock the row`} />
  </div>
);}

function P10() { return (
  <div>
    <H2>10.1 Normalization — splitting data up properly</H2>
    <Story>In a single register you wrote the student's name, his class, and the class teacher's phone number. The class has 40 children, so the teacher's phone number is written 40 times. The teacher changes her number? You must edit 40 places. Miss one and your data is wrong. The fix: put the teacher's details in a separate register and link to it with an ID. That is normalization.</Story>
    <DTable
      headers={["Form","Rule","In plain words"]}
      rows={[
        [<strong style={{color:T.accent}}>1NF</strong>,"Every cell holds a single (atomic) value; no repeating groups","Don't write '9111,9222' in one column — make separate rows"],
        [<strong style={{color:T.green}}>2NF</strong>,"1NF + no partial dependency","With a composite key, move out anything that depends on only part of it"],
        [<strong style={{color:T.yellow}}>3NF</strong>,"2NF + no transitive dependency","A non-key column must not depend on another non-key column"],
        [<strong style={{color:ORA}}>BCNF</strong>,"A stricter version of 3NF","Every determinant must be a candidate key"],
      ]}
    />
    <Note color={T.yellow}><strong>Benefits:</strong> less redundancy, no update anomalies, less storage.<br/><strong>Cost:</strong> more tables means more joins, which can slow reports. That's why reporting systems often <strong>de-normalize on purpose</strong>.</Note>

    <H2>10.2 Relationships and the ER Model</H2>
    <DTable
      headers={["Relationship","Example","How it is implemented"]}
      rows={[
        ["One-to-One","Person and passport","FK + a UNIQUE constraint"],
        ["One-to-Many","Class to students","FK on the 'many' table"],
        ["Many-to-Many","Students and subjects","A junction table in between"],
      ]}
    />
    <P>An <strong style={{color:T.textPri}}>ER diagram</strong> is the blueprint you draw before creating any table. Entity (table) → Attribute (column) → Relationship (line).</P>

    <H2>10.3 Users, Privileges, Roles (DCL)</H2>
    <CodeBlock code={`CREATE USER sumit IDENTIFIED BY password123;
GRANT CREATE SESSION, CREATE TABLE TO sumit;      -- system privilege
GRANT SELECT, INSERT ON student TO sumit;         -- object privilege
REVOKE INSERT ON student FROM sumit;

CREATE ROLE app_reader;                           -- a role is a bundle
GRANT SELECT ON student TO app_reader;
GRANT app_reader TO sumit;                        -- 1 role, 100 users, easy`} />
    <Note color={T.green}><strong>Why roles?</strong> Granting 10 permissions to 50 users individually = 500 commands. Creating one role and granting it to 50 users = 51 commands.</Note>
  </div>
);}

function PPractice() { return (
  <div>
    <P>Run every example in this guide against this schema. Use Oracle XE (free) or <strong style={{color:T.accent}}>Oracle Live SQL</strong> at livesql.oracle.com — SQL runs right in the browser with nothing to install.</P>
    <CodeBlock code={`CREATE TABLE class (
    class_id   NUMBER(3) PRIMARY KEY,
    class_name VARCHAR2(10) NOT NULL,
    teacher    VARCHAR2(50)
);
CREATE TABLE student (
    roll_no   NUMBER(4) PRIMARY KEY,
    name      VARCHAR2(50) NOT NULL,
    email     VARCHAR2(60) UNIQUE,
    gender    CHAR(1) DEFAULT 'M' CHECK (gender IN ('M','F','O')),
    marks     NUMBER(5,2) CHECK (marks BETWEEN 0 AND 100),
    join_date DATE DEFAULT SYSDATE,
    class_id  NUMBER(3) REFERENCES class(class_id)
);
INSERT INTO class VALUES (7,'7-A','Meena');
INSERT INTO class VALUES (8,'8-B','Suresh');
INSERT INTO class VALUES (9,'9-C','Anil');
INSERT INTO student (roll_no,name,email,gender,marks,class_id)
     VALUES (1,'Rahul','rahul@abc.com','M',85,7);
INSERT INTO student (roll_no,name,email,gender,marks,class_id)
     VALUES (2,'Priya','priya@abc.com','F',92,8);
INSERT INTO student (roll_no,name,email,gender,marks,class_id)
     VALUES (3,'Amit','amit@abc.com','M',NULL,NULL);
COMMIT;`} />

    <H2>Exercises</H2>
    {[
      "List every student in class 7.",
      "Show the average marks and student count for each class.",
      "Find students who have no class assigned (do it two ways — with a LEFT JOIN, and with NOT EXISTS).",
      "Write a trigger that raises an error if marks go above 100.",
      "Write a function that takes a roll_no and returns the grade.",
      "Create a view that shows only students scoring above 80.",
    ].map((ex, i) => (
      <div key={i} style={{display:"flex",gap:12,padding:"10px 14px",marginBottom:8,
        background:T.card,borderRadius:8,border:`1px solid ${T.border}`}}>
        <span style={{color:T.accent,fontWeight:700,minWidth:24}}>{i+1}.</span>
        <span style={{color:T.textSec,fontSize:13.5,lineHeight:1.7}}>{ex}</span>
      </div>
    ))}
  </div>
);}

function PRoadmap() {
  const weeks = [
    {w:"Week 1", t:"Part 0–1: DB, RDBMS, Oracle basics, tables, datatypes, NULL", g:"Create your first table"},
    {w:"Week 2", t:"Part 2–3: Keys, constraints", g:"Design a table that bad data cannot enter"},
    {w:"Week 3", t:"Part 4: DDL, DML, SELECT, WHERE, ORDER BY", g:"Put data in and pull it out"},
    {w:"Week 4", t:"Part 5: Functions, GROUP BY, HAVING", g:"Build reports"},
    {w:"Week 5", t:"Part 6: Joins, subqueries, set operators", g:"The most important week — real SQL starts here"},
    {w:"Week 6", t:"Part 7: Views, indexes, sequences", g:"Understand database objects"},
    {w:"Week 7", t:"Part 8: PL/SQL, procedures, functions, triggers", g:"Programming inside the database"},
    {w:"Week 8", t:"Part 9–10: Transactions, ACID, normalization, roles", g:"Design and safety"},
  ];
  const cards = [
    ["Table","A register of rows and columns"],
    ["Row","One record — one child's line"],
    ["Column","One field — one heading"],
    ["Primary Key","The roll number — unique and never NULL"],
    ["Foreign Key","The chain that links to another table"],
    ["Constraint","A school rule the database itself enforces"],
    ["NULL","'Unknown' — not zero"],
    ["DDL / DML / DQL / DCL / TCL","Structure / data / reading / permissions / commit"],
    ["Function","A juicer — put input in, get one value out"],
    ["Aggregate","One answer for a whole group"],
    ["GROUP BY","Split rows into piles"],
    ["WHERE vs HAVING","Filter rows vs filter groups"],
    ["Join","Combining two registers on a common column"],
    ["INNER vs OUTER","Matches only vs keep rows even without a match"],
    ["Subquery","A query inside a query"],
    ["View","A saved query — virtual, stores no data"],
    ["Index","A book's index page — makes search fast"],
    ["Sequence","The bank's token machine"],
    ["PL/SQL","SQL plus if-else and loops"],
    ["Procedure vs Function","Do a job vs bring back a value"],
    ["Trigger","The automatic light — event happens, action follows by itself"],
    [":OLD / :NEW","The old value / the new value in a trigger"],
    ["Transaction","The ATM — all or nothing"],
    ["ACID","Atomicity, Consistency, Isolation, Durability"],
    ["COMMIT / ROLLBACK","Make it final / take it back"],
    ["Normalization","Remove repeated data, split into tables"],
    ["Role","A bundle of privileges"],
  ];

  return (
    <div>
      <H2>Study Roadmap — 8 Weeks</H2>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:32}}>
        {weeks.map((row,i)=>(
          <div key={i} style={{display:"flex",gap:12,background:T.card,borderRadius:10,
            padding:"12px 16px",border:`1px solid ${T.border}`,alignItems:"flex-start"}}>
            <span style={{fontSize:11,fontWeight:700,color:T.accent,minWidth:56,
              letterSpacing:.5,textTransform:"uppercase",paddingTop:2}}>{row.w}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:13.5,color:T.textPri,marginBottom:3}}>{row.t}</div>
              <div style={{fontSize:12,color:T.green}}>Goal: {row.g}</div>
            </div>
          </div>
        ))}
      </div>

      <H2>Revision Card — Everything in One Line</H2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:8}}>
        {cards.map(([concept,def],i)=>(
          <div key={i} style={{background:T.card,borderRadius:8,padding:"10px 14px",
            border:`1px solid ${T.border}`,display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:12,fontWeight:700,color:ORA,minWidth:140,lineHeight:1.5}}>{concept}</span>
            <span style={{fontSize:12,color:T.textSec,lineHeight:1.6}}>{def}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SIDEBAR METADATA ──────────────────────────────────────────────────────────

const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",                sub:"What this guide covers"},
  {id:"p0",   icon:"🧱", title:"Part 0 — Foundation",    sub:"DB, DBMS, RDBMS, Oracle, Schema"},
  {id:"p1",   icon:"📋", title:"Part 1 — Tables",        sub:"Rows, columns, datatypes, NULL"},
  {id:"p2",   icon:"🔑", title:"Part 2 — Keys",          sub:"PK, FK, Composite, Surrogate"},
  {id:"p3",   icon:"📏", title:"Part 3 — Constraints",   sub:"NOT NULL, UNIQUE, CHECK, FK rules"},
  {id:"p4",   icon:"💬", title:"Part 4 — SQL Language",  sub:"DDL, DML, SELECT, execution order"},
  {id:"p5",   icon:"⚙️", title:"Part 5 — Functions",     sub:"String, Number, Date, Aggregate, Window"},
  {id:"p6",   icon:"🔗", title:"Part 6 — JOINs",         sub:"Inner, Outer, Self, Subqueries, Set ops"},
  {id:"p7",   icon:"🗂️", title:"Part 7 — DB Objects",    sub:"Views, Indexes, Sequences, Synonyms"},
  {id:"p8",   icon:"⚡", title:"Part 8 — PL/SQL",        sub:"Blocks, Cursors, Procedures, Triggers"},
  {id:"p9",   icon:"💳", title:"Part 9 — Transactions",  sub:"ACID, COMMIT, ROLLBACK, Locking"},
  {id:"p10",  icon:"🏗️", title:"Part 10 — Design",      sub:"Normalization, ER, DCL, Roles"},
  {id:"prac", icon:"🧪", title:"Practice Schema",        sub:"Full setup + 6 exercises"},
  {id:"road", icon:"🗺️", title:"Roadmap & Revision",    sub:"8-week plan + revision card"},
];

const sectionComponents = {
  p0:<P0/>, p1:<P1/>, p2:<P2/>, p3:<P3/>, p4:<P4/>,
  p5:<P5/>, p6:<P6/>, p7:<P7/>, p8:<P8/>, p9:<P9/>,
  p10:<P10/>, prac:<PPractice/>, road:<PRoadmap/>,
};

function Overview({ onNav }) {
  const cards = [
    {id:"p0",  icon:"🧱", title:"Part 0 — Foundation",   sub:"DB, DBMS, RDBMS, Oracle basics, Schema"},
    {id:"p1",  icon:"📋", title:"Part 1 — Tables",       sub:"Rows, columns, datatypes, NULL"},
    {id:"p2",  icon:"🔑", title:"Part 2 — Keys",         sub:"PK, FK, Composite, Surrogate keys"},
    {id:"p3",  icon:"📏", title:"Part 3 — Constraints",  sub:"NOT NULL, UNIQUE, CHECK, FK delete rules"},
    {id:"p4",  icon:"💬", title:"Part 4 — SQL Language", sub:"DDL, DML, SELECT & execution order"},
    {id:"p5",  icon:"⚙️", title:"Part 5 — Functions",    sub:"String, Number, Date, Aggregate, Window"},
    {id:"p6",  icon:"🔗", title:"Part 6 — JOINs",        sub:"Inner, Outer, Self, Subqueries, Set ops"},
    {id:"p7",  icon:"🗂️", title:"Part 7 — DB Objects",   sub:"Views, Indexes, Sequences, Synonyms"},
    {id:"p8",  icon:"⚡", title:"Part 8 — PL/SQL",       sub:"Blocks, Cursors, Procedures, Functions, Triggers"},
    {id:"p9",  icon:"💳", title:"Part 9 — Transactions", sub:"ACID, COMMIT, ROLLBACK, Locking"},
    {id:"p10", icon:"🏗️", title:"Part 10 — Design",     sub:"Normalization, ER Model, Users & Roles"},
    {id:"prac",icon:"🧪", title:"Practice Schema",       sub:"Full working schema + 6 exercises"},
    {id:"road",icon:"🗺️", title:"Roadmap & Revision",   sub:"8-week study plan + revision card"},
  ];
  return (
    <div>
      <div style={{marginBottom:8,fontSize:11,letterSpacing:2,textTransform:"uppercase",color:ORA,fontWeight:700}}>
        DATABASE · ORACLE · RDBMS
      </div>
      <h1 style={{fontSize:26,fontWeight:800,color:T.textPri,margin:"0 0 10px",lineHeight:1.2}}>
        Database Zero to Hero
      </h1>
      <p style={{fontSize:14,color:T.textSec,lineHeight:1.8,margin:"0 0 6px"}}>
        RDBMS + Oracle — explained simply enough for a 12-year-old to follow.
      </p>
      <p style={{fontSize:13,color:T.textMute,lineHeight:1.7,margin:"0 0 20px"}}>
        Every topic has a <strong style={{color:"#A78BFA"}}>Story</strong> (analogy), a <strong style={{color:T.accent}}>Definition</strong> (exam-ready), and an <strong style={{color:ORA}}>Oracle example</strong>. The order is sequential — each part stands on the one before it.
      </p>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:12}}>
        {cards.map(c=>(
          <button key={c.id} onClick={()=>onNav(c.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:12,
              padding:"14px 16px",textAlign:"left",cursor:"pointer",transition:"border-color .15s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor=ORA}
            onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>
            <div style={{fontSize:20,marginBottom:8}}>{c.icon}</div>
            <div style={{fontSize:13,fontWeight:700,color:T.textPri,marginBottom:4}}>{c.title}</div>
            <div style={{fontSize:12,color:T.textSec}}>{c.sub}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────────────

export default function DatabaseZeroToHero() {
  const [active, setActive] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const mainRef = useRef(null);

  const totalSections = SECTIONS.length - 1;
  const pct = Math.round((visited.size / totalSections) * 100);

  function nav(id) {
    setActive(id);
    setVisited(v => new Set([...v, id]));
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }

  return (
    <div style={{display:"flex",height:"calc(100vh - 70px)",background:T.bg,
      color:T.textPri,fontFamily:"system-ui,sans-serif",overflow:"hidden"}}>

      {/* Sidebar */}
      <div style={{width:240,minWidth:200,borderRight:`1px solid ${T.border}`,
        display:"flex",flexDirection:"column",background:T.surface,overflowY:"auto",flexShrink:0}}>
        <div style={{padding:"14px 14px 0"}}>
          <div style={{fontSize:11,fontWeight:700,letterSpacing:1,textTransform:"uppercase",
            color:ORA,marginBottom:10}}>🗄️ DB Zero to Hero</div>
          <div style={{height:4,background:T.border,borderRadius:4,marginBottom:4}}>
            <div style={{height:"100%",borderRadius:4,
              background:`linear-gradient(90deg,${ORA},${T.accent})`,
              width:`${pct}%`,transition:"width .3s"}}/>
          </div>
          <div style={{fontSize:10,color:T.textMute,marginBottom:12}}>{pct}% explored</div>
        </div>
        <nav style={{flex:1,padding:"0 8px 16px"}}>
          {SECTIONS.map(s => {
            const isActive = active===s.id;
            const isDone = visited.has(s.id) && !isActive;
            return (
              <button key={s.id} onClick={()=>nav(s.id)}
                style={{width:"100%",textAlign:"left",background:isActive?`${ORA}15`:"none",
                  border:"none",borderRadius:8,padding:"8px 10px",cursor:"pointer",marginBottom:2,
                  borderLeft:isActive?`3px solid ${ORA}`:"3px solid transparent"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13}}>{s.icon}</span>
                  <span style={{fontSize:12,fontWeight:isActive?700:500,
                    color:isActive?ORA:T.textPri,lineHeight:1.3,flex:1}}>{s.title}</span>
                  {isDone && <span style={{fontSize:10,color:T.green}}>✓</span>}
                </div>
                <div style={{fontSize:10,color:T.textMute,marginTop:1,paddingLeft:19}}>{s.sub}</div>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {/* Progress bar */}
        <div style={{flexShrink:0,background:T.bg,padding:"8px 40px 10px",
          borderBottom:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:14}}>{SECTIONS.find(s=>s.id===active)?.icon}</span>
          <span style={{fontSize:13,fontWeight:600,color:T.textPri}}>
            {active==="home"?"Database Zero to Hero":SECTIONS.find(s=>s.id===active)?.title}
          </span>
          <div style={{flex:1,height:3,background:T.border,borderRadius:2}}>
            <div style={{height:"100%",borderRadius:2,
              background:`linear-gradient(90deg,${ORA},${T.accent})`,
              width:`${pct}%`,transition:"width .3s"}}/>
          </div>
          <span style={{fontSize:11,color:T.textMute,minWidth:28,textAlign:"right"}}>{pct}%</span>
        </div>

        {/* Scrollable content */}
        <div ref={mainRef} style={{flex:1,overflowY:"auto",padding:"32px 48px"}}>
          {active==="home"
            ? <Overview onNav={nav}/>
            : <div>
                <h2 style={{fontSize:20,fontWeight:700,color:T.textPri,margin:"0 0 20px"}}>
                  {SECTIONS.find(s=>s.id===active)?.title}
                </h2>
                {sectionComponents[active]}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:36,
                  paddingTop:16,borderTop:`1px solid ${T.border}`}}>
                  {(()=>{
                    const idx=SECTIONS.findIndex(s=>s.id===active);
                    const prev=SECTIONS[idx-1];
                    const next=SECTIONS[idx+1];
                    return (<>
                      {prev
                        ? <button onClick={()=>nav(prev.id)} style={{background:T.card,
                            border:`1px solid ${T.border}`,borderRadius:8,padding:"8px 16px",
                            cursor:"pointer",fontSize:12,color:T.textSec,fontFamily:"inherit"}}>
                            ← {prev.title}
                          </button>
                        : <span/>}
                      {next
                        ? <button onClick={()=>nav(next.id)} style={{background:ORA,border:"none",
                            borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:12,
                            color:"#0B0F1A",fontWeight:700,fontFamily:"inherit"}}>
                            {next.title} →
                          </button>
                        : <span/>}
                    </>);
                  })()}
                </div>
              </div>
          }
        </div>
      </div>
    </div>
  );
}
